import { getThumbnailUrlWithToken } from "connection/mux";
import { ImgHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";

// Mux streams with jwt privacy enabled still require jwt signatures for their thumbnails
// this will check if the image requires a jwt token and async update the src of the image
// element as soon as the jwt is loaded in. It also forces the image to be lazy loaded
export default function VideoThumbnail(
	props: ImgHTMLAttributes<HTMLImageElement> & {
		errorElement?: React.ReactNode;
		loadingElement?: React.ReactNode;
	}
) {
	const [src, setSrc] = useState(props.src);
	const [ready, setReady] = useState(false);
	const [errored, setErrored] = useState(false);
	const onErrorRef = useRef(props.onError);

	useEffect(() => {
		onErrorRef.current = props.onError;
	}, [props.onError]);

	useEffect(() => {
		if (props.src) {
			getThumbnailUrlWithToken(props.src)
				.then((res: string | Error) => {
					if (res instanceof Error) {
						console.error("Error fetching thumbnail URL:", res);
						setErrored(true);
						return;
					}
					setSrc(res);
					setErrored(false);
				})
				.finally(() => setReady(true));
		}
	}, [props.src]);

	const handleError = useCallback((e) => {
		setErrored(true);
		if (onErrorRef.current) {
			onErrorRef.current(e);
		}
	}, []);

	if (!ready && props.loadingElement) {
		return <>{props.loadingElement}</>;
	}

	if (errored && props.errorElement) {
		return <>{props.errorElement}</>;
	}

	return (
		<img
			{...props}
			src={src}
			loading="lazy"
			onError={ready ? handleError : undefined}
		/>
	);
}